import { Link } from "react-router-dom";
import { Mail, Trash2, Info, ArrowRight } from "lucide-react";
import { MaskedLine, Reveal, usePageTitle } from "../components/Motion";
import { Seo } from "../components/Seo";
import { SITE } from "../site";

const ContactPage = () => {
    usePageTitle("Contact — SahiKhoj");
    return (
        <>
            <Seo
                title="Contact — SahiKhoj"
                description="Contact the SahiKhoj team for app support, feedback and data requests via the official support email."
                path="/contact"
            />
            <section data-testid="contact-hero" className="hero-glow bg-ink pb-20 pt-40 text-cream sm:pb-28 lg:pt-48">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        Contact us
                    </p>
                    <h1 className="mt-7 max-w-4xl font-display text-5xl font-semibold leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.2}>Talk to</MaskedLine>
                        <MaskedLine delay={0.35} className="text-saffron">
                            SahiKhoj.
                        </MaskedLine>
                    </h1>
                    <p className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg">
                        Questions about the app, feedback, or data requests — reach us
                        through the official support channel below.
                    </p>
                </div>
            </section>

            <section className="bg-cream py-24 sm:py-32">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
                    <Reveal className="lg:col-span-7">
                        <div
                            data-testid="contact-email-card"
                            className="h-full rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-12"
                        >
                            <span className="inline-flex items-center gap-2 rounded-full bg-saffron-soft px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-saffron">
                                <Mail className="h-3.5 w-3.5" />
                                Official support
                            </span>
                            <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                                Official support email
                            </h2>
                            <a
                                href={`mailto:${SITE.supportEmail}`}
                                data-testid="contact-support-email"
                                className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-saffron/30 bg-saffron-soft/60 px-6 py-5 transition-colors hover:border-saffron"
                            >
                                <Mail className="h-5 w-5 shrink-0 text-saffron" />
                                <span className="font-display text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                                    {SITE.supportEmail}
                                </span>
                            </a>
                            <p className="mt-5 max-w-lg text-sm leading-relaxed text-smoke">
                                Write to us for app support, feedback or data requests.
                                Please include your registered email address so we can
                                help you faster.
                            </p>
                        </div>
                    </Reveal>
                    <div className="flex flex-col gap-6 lg:col-span-5">
                        <Reveal delay={0.1}>
                            <div className="rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-10">
                                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-india/10">
                                    <Trash2 className="h-5 w-5 text-india" />
                                </span>
                                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-ink">
                                    Account or data deletion
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-smoke">
                                    To request deletion of your SahiKhoj app account and
                                    associated personal data, follow the steps on our
                                    dedicated page.
                                </p>
                                <Link
                                    to="/delete-account"
                                    data-testid="contact-delete-account-link"
                                    className="group mt-5 inline-flex items-center gap-2 text-sm font-medium text-india transition-colors hover:text-saffron"
                                >
                                    Delete account / data request
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="rounded-3xl border border-ink/5 bg-white p-8 shadow-sm sm:p-10">
                                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-saffron-soft">
                                    <Info className="h-5 w-5 text-saffron" />
                                </span>
                                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-ink">
                                    A quick note
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-smoke">
                                    Many SahiKhoj features — offers, coupons, price
                                    comparison and travel search — work on this website
                                    too. Purchases and bookings are always completed on
                                    the merchant or booking platform; SahiKhoj never
                                    processes your payment.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ContactPage;
